import React, { useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { STORAGE } from '../constants/storageKeys';
import { usePersistentState } from '../hooks/usePersistentState';
import { useNow } from '../hooks/useNow';
import { formatMsClock } from '../utils/time';
import './Timer.css';

const PRESETS_MS = [
  ['45s', 45_000],
  ['60s', 60_000],
  ['90s', 90_000],
  ['3m', 180_000],
  ['5m', 300_000],
  ['15m', 900_000],
];

const defaultTimer = {
  mode: 'turn',
  durationMs: 60_000,
  status: 'idle',
  endAt: null,
  pausedRemainingMs: null,
  finishedAt: null,
  passPhone: false,
};

function modeDefaultMs(mode) {
  if (mode === 'break') return 300_000;
  if (mode === 'custom') return 120_000;
  return 60_000;
}

function remainingFromState(t, now) {
  if (t.status === 'running' && t.endAt) return Math.max(0, t.endAt - now);
  if (t.status === 'paused' && t.pausedRemainingMs != null) return t.pausedRemainingMs;
  if (t.status === 'done') return 0;
  return t.durationMs;
}

export default function Timer() {
  const [timer, setTimer] = usePersistentState(STORAGE.timer, defaultTimer);
  const now = useNow(250);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      setTimer((t) => {
        if (t.status !== 'running' || !t.endAt) return t;
        if (Date.now() >= t.endAt) {
          return { ...t, status: 'done', finishedAt: Date.now(), endAt: null, pausedRemainingMs: null };
        }
        return t;
      });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [setTimer]);

  useEffect(() => {
    if (timer.status !== 'running' || !timer.endAt) return undefined;
    if (now < timer.endAt) return undefined;
    setTimer((t) =>
      t.status === 'running' && t.endAt && now >= t.endAt
        ? { ...t, status: 'done', finishedAt: Date.now(), endAt: null, pausedRemainingMs: null }
        : t
    );
    return undefined;
  }, [now, timer.status, timer.endAt, setTimer]);

  const displayMs = useMemo(() => remainingFromState(timer, now), [timer, now]);

  const setMode = (mode) => {
    if (timer.status === 'running' || timer.status === 'paused') return;
    const d = modeDefaultMs(mode);
    setTimer((t) => ({ ...t, mode, durationMs: d, status: 'idle', endAt: null, pausedRemainingMs: null, finishedAt: null }));
  };

  const pickPreset = (ms) => {
    if (timer.passPhone && (timer.status === 'running' || timer.status === 'paused')) return;
    if (timer.status === 'running' || timer.status === 'paused') return;
    setTimer((t) => ({ ...t, durationMs: ms, status: 'idle', endAt: null, pausedRemainingMs: null, finishedAt: null }));
  };

  const start = useCallback(() => {
    setTimer((t) => {
      const rem = t.status === 'paused' && t.pausedRemainingMs != null ? t.pausedRemainingMs : t.durationMs;
      return {
        ...t,
        status: 'running',
        endAt: Date.now() + rem,
        pausedRemainingMs: null,
        finishedAt: null,
      };
    });
  }, [setTimer]);

  const pause = useCallback(() => {
    setTimer((t) => {
      if (t.status !== 'running' || !t.endAt) return t;
      const rem = Math.max(0, t.endAt - Date.now());
      return { ...t, status: 'paused', pausedRemainingMs: rem, endAt: null };
    });
  }, [setTimer]);

  const reset = useCallback(() => {
    setTimer((t) => ({
      ...defaultTimer,
      mode: t.mode,
      durationMs: modeDefaultMs(t.mode),
      passPhone: t.passPhone,
    }));
  }, [setTimer]);

  const addMinute = useCallback(() => {
    setTimer((t) => {
      if (t.status === 'running' && t.endAt) return { ...t, endAt: t.endAt + 60_000 };
      if (t.status === 'paused' && t.pausedRemainingMs != null) return { ...t, pausedRemainingMs: t.pausedRemainingMs + 60_000 };
      if (t.status === 'done') return { ...t, status: 'running', endAt: Date.now() + 60_000, pausedRemainingMs: null, finishedAt: null };
      return { ...t, durationMs: t.durationMs + 60_000 };
    });
  }, [setTimer]);

  const stopAlarm = useCallback(() => {
    setTimer((t) => ({
      ...defaultTimer,
      mode: t.mode,
      durationMs: t.durationMs,
      passPhone: t.passPhone,
    }));
  }, [setTimer]);

  const alarmAddMinute = useCallback(() => {
    setTimer((t) => ({
      ...t,
      status: 'running',
      endAt: Date.now() + 60_000,
      pausedRemainingMs: null,
      finishedAt: null,
    }));
  }, [setTimer]);

  return (
    <div className="timer-two">
      <Link to="/" className="kit-back-link" style={{ gridColumn: '1 / -1' }}>
        ← Game Night Kit
      </Link>
      <h1 className="screen-title" style={{ gridColumn: '1 / -1' }}>
        Timer
      </h1>

      <div className="screen-panel">
        <div className="timer-chips">
          {['turn', 'break', 'custom'].map((m) => (
            <button
              type="button"
              key={m}
              className={`dice-chip ${timer.mode === m ? 'active' : ''}`}
              onClick={() => setMode(m)}
              disabled={timer.status === 'running' || timer.status === 'paused'}
            >
              {m === 'turn' ? 'Turn' : m === 'break' ? 'Break' : 'Custom'}
            </button>
          ))}
        </div>

        <div className="timer-hero" aria-live="polite">
          {formatMsClock(displayMs)}
        </div>

        <div className="timer-presets">
          {PRESETS_MS.map(([label, ms]) => (
            <button type="button" key={label} className="dice-chip" onClick={() => pickPreset(ms)}>
              {label}
            </button>
          ))}
        </div>

        {timer.mode === 'custom' && timer.status === 'idle' && (
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="custom-min" style={{ fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Custom duration (minutes)
            </label>
            <input
              id="custom-min"
              type="number"
              min="1"
              max="120"
              step="1"
              style={{ width: '100%', padding: 12, borderRadius: 12, border: '2px solid var(--glass-border)', background: 'rgba(0,0,0,0.35)', color: 'white', fontWeight: 700 }}
              value={Math.max(1, Math.round(timer.durationMs / 60_000))}
              onChange={(e) => {
                const m = Math.min(120, Math.max(1, parseInt(e.target.value, 10) || 1));
                setTimer((t) => ({ ...t, durationMs: m * 60_000 }));
              }}
            />
          </div>
        )}

        <div className="toggle-row">
          <input
            type="checkbox"
            id="pass-phone"
            checked={timer.passPhone}
            onChange={(e) => setTimer((t) => ({ ...t, passPhone: e.target.checked }))}
          />
          <label htmlFor="pass-phone">Pass-phone lock (ignore preset taps while running)</label>
        </div>

        <div className="timer-btn-row">
          {timer.status === 'idle' || timer.status === 'done' ? (
            <button type="button" className="timer-btn-primary" style={{ flex: 2 }} onClick={start}>
              Start
            </button>
          ) : timer.status === 'running' ? (
            <button type="button" className="timer-btn-primary" onClick={pause}>
              Pause
            </button>
          ) : (
            <button type="button" className="timer-btn-primary" onClick={start}>
              Resume
            </button>
          )}
          <button type="button" onClick={addMinute}>
            +1 min
          </button>
          <button type="button" onClick={reset}>
            Reset
          </button>
        </div>
      </div>

      <div className="screen-panel">
        <p style={{ color: 'var(--text-secondary)', fontWeight: 600, margin: 0 }}>
          Tip: timer keeps correct time when you switch apps (uses your device clock).
        </p>
      </div>

      {timer.status === 'done' && (
        <div className="timer-alarm" role="alertdialog" aria-modal="true" aria-labelledby="alarm-title">
          <h2 id="alarm-title" className="screen-title" style={{ fontSize: '2.5rem', color: 'var(--accent-gold)' }}>
            Time&apos;s up!
          </h2>
          <div className="timer-btn-row" style={{ maxWidth: 400, marginTop: 24 }}>
            <button type="button" className="timer-btn-primary" onClick={stopAlarm}>
              Stop
            </button>
            <button type="button" onClick={alarmAddMinute}>
              +1 min
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
